import { z } from 'zod';
/**
 * The one-time import of a Lemniscus export (`.claude/rules/import.md`): what
 * the practice maps in the preview, what the preview answers, and the issues
 * it lists.
 *
 * An export is two files — `Aktenexport.xlsx`, one row per record (Akte), and
 * a ZIP with one folder per record. The server reads both; nothing here
 * carries a path or a file's bytes.
 */
/**
 * Everything the preview can say about an export, as a code. The sentence
 * belongs to the screen (`strings.ts`), so a code is all that travels.
 *
 * An **error** blocks the import; a **hint** says what will not be imported
 * or what looks odd, and blocks nothing.
 */
export const lemniscusErrorCodes = [
    /** The xlsx cannot be read, or has no sheet "Akten". */
    'sheet_unreadable',
    /** The sheet "Akten" holds no record. */
    'sheet_empty',
    /** A column the import needs is missing; `field` names it. */
    'sheet_missing_column',
    /** The ZIP cannot be read; `value` says why (see `lemniscusArchiveProblems`). */
    'archive_unreadable',
    /** "Nummer" is empty or not a whole number the contact number can hold. */
    'number_invalid',
    /** Two records share one number. */
    'number_duplicate',
    /** A folder of the ZIP has no row in the xlsx. */
    'folder_without_record',
    /** Two folders carry the same number. */
    'folder_duplicate',
    /** The folder's name is not the record's name. */
    'name_mismatch',
    /** A person without a last name, a company without a name; `field`. */
    'required_missing',
    /** `field` is longer than praxi stores. */
    'field_too_long',
    /** A date or a time that does not exist; `field`, and `line` in a CSV. */
    'date_invalid',
    /** `behandlungsverlauf.csv` cannot be read. */
    'csv_unreadable',
    /** `behandlungsverlauf.csv` lacks a column; `field` names it. */
    'csv_missing_column',
    /** "Dauer" is not a number of minutes an appointment can have. */
    'duration_invalid',
    /** "Dauer" is empty and the activity type has no default duration. */
    'duration_missing',
    /** A value of "Akten-Typ" has no role chosen; `value`. */
    'record_type_unmapped',
    /** A value of "Geschlecht" has no entry chosen; `value`. */
    'gender_unmapped',
    /** A value of "Terminbezeichnung" has no activity type chosen; `value`. */
    'appointment_name_unmapped',
    /** No note type chosen for the notes (`field` `noteTypeId`) or for the
     *  invoices (`invoiceNoteTypeId`). */
    'note_type_unmapped',
    /** One file is larger than an attachment can be at all. */
    'file_too_large',
    /** Unpacked, the export is larger than `IMPORT_UNPACKED_MAX_MB`. */
    'unpacked_too_large',
    /** The volume has too little free space for the unpacked export. */
    'disk_space',
];
export const lemniscusHintCodes = [
    /** A row of the xlsx has no folder: master data only. */
    'record_without_folder',
    /** Attachments whose note PDF is missing; the note is created all the same. */
    'attachment_without_note',
    /** An appointment names an invoice whose PDF is not in the export. */
    'invoice_without_pdf',
    /** An invoice PDF no appointment names. */
    'invoice_without_appointment',
    /** `count` attachments are larger than `NOTE_FILE_MAX_MB`; imported anyway. */
    'attachment_over_limit',
    /** `field` has content in `count` records and is not imported. */
    'field_not_imported',
    /** The address is not one; the field stays empty. */
    'email_invalid',
    /** `value` is not in the country catalogue; the field stays empty. */
    'country_unknown',
    /** `count` files of the ZIP are neither notes nor invoices nor the CSV. */
    'unknown_file',
    /** `count` files hold no bytes and cannot be attached. */
    'empty_file',
    /** A note names an appointment (`value`, as its PDF writes it) at which no
     *  activity of the record starts; the note stays unlinked. */
    'note_appointment_not_found',
    /** More than one activity of the record starts at the appointment a note
     *  names (`value`); the note stays unlinked. */
    'note_appointment_ambiguous',
    /** The appointment line of a note's PDF (`value`) is not a date and a time
     *  that exist; the note stays unlinked. */
    'note_appointment_unreadable',
    /** The sheet `value` ("Notizen", "Termine") lacks the column `field`, so it
     *  is not used (#67). The sheets are optional; nothing blocks the import. */
    'extra_sheet_missing_column',
    /** `count` rows of "Notizen" belong to no note PDF of their record — none has
     *  that title at that moment; their text is not imported. */
    'note_text_unmatched',
    /** `count` rows of "Notizen" fit more than one note PDF, or share one PDF
     *  with another row; their text is not imported. */
    'note_text_ambiguous',
    /** `count` note texts are longer than a note's text can be. */
    'note_text_too_long',
    /** `count` remarks of "Termine" belong to no line of the record's
     *  `behandlungsverlauf.csv` that starts at that moment. */
    'appointment_remark_unmatched',
    /** `count` remarks of "Termine" fit more than one line of the CSV, or share
     *  one with another row. */
    'appointment_remark_ambiguous',
    /** `count` remarks would make an activity's internal note longer than it
     *  can be; the remark is not imported. */
    'appointment_remark_too_long',
    /** "Diagnose" of "Termine" has content in `count` rows; an activity has no
     *  such field, so it is not imported. */
    'appointment_diagnosis_not_imported',
];
export const lemniscusIssueCodes = [...lemniscusErrorCodes, ...lemniscusHintCodes];
/** Why a ZIP is refused — the `value` of `archive_unreadable`. */
export const lemniscusArchiveProblems = [
    'not_a_zip',
    'encrypted',
    'compression',
    'symlink',
    'too_many_entries',
    'path_too_long',
    'no_records',
];
export const lemniscusIssueSchema = z.object({
    severity: z.enum(['error', 'hint']),
    code: z.enum(lemniscusIssueCodes),
    /** The record the issue is about, as its number and name read in the
     *  export; both null on an issue about the export as a whole. */
    recordNumber: z.string().nullable(),
    recordName: z.string().nullable(),
    /** The line of `behandlungsverlauf.csv`, counting the header as 1. */
    line: z.number().int().nullable(),
    /** The column or setting concerned, in the export's own spelling. */
    field: z.string().nullable(),
    value: z.string().nullable(),
    count: z.number().int().nullable(),
});
const mappingEntry = z.object({ value: z.string().max(400), id: z.uuid().nullable() });
/**
 * What the practice chooses in the preview. Every value that occurs in the
 * export needs an entry before the import starts; while one is missing the
 * preview lists it as an error, so the same body serves both requests.
 */
export const lemniscusMappingSchema = z.object({
    /** "Akten-Typ" → `contact_role_type`. */
    recordTypes: z.array(mappingEntry).max(500).default([]),
    /** "Geschlecht" → `gender`. */
    genders: z.array(mappingEntry).max(500).default([]),
    /** "Terminbezeichnung" → `activity_type`. */
    appointmentNames: z.array(mappingEntry).max(2000).default([]),
    /** The note type of the imported notes, and of the notes that hold an
     *  invoice PDF. */
    noteTypeId: z.uuid().nullable().default(null),
    invoiceNoteTypeId: z.uuid().nullable().default(null),
});
const mappingValue = z.object({
    value: z.string(),
    /** How many records, or appointments, carry it. */
    count: z.number().int(),
    /** The catalogue entry praxi would pick, null where none reads alike. */
    suggestedId: z.uuid().nullable(),
});
/** How many issues a preview lists at most; `errorCount` and `hintCount`
 *  count all of them. */
export const LEMNISCUS_ISSUE_LIMIT = 500;
export const lemniscusPreviewSchema = z.object({
    counts: z.object({
        records: z.number().int(),
        persons: z.number().int(),
        organizations: z.number().int(),
        appointmentsPast: z.number().int(),
        appointmentsFuture: z.number().int(),
        notes: z.number().int(),
        /** How many of `notes` are linked to an activity: the documentation of
         *  an appointment, by the appointment its PDF names. */
        notesLinked: z.number().int(),
        /** How many of `notes` get the text of a row of the sheet "Notizen" (#67). */
        notesWithText: z.number().int(),
        /** How many appointments get the remark of a row of the sheet "Termine"
         *  in their activity's internal note (#67). */
        appointmentRemarks: z.number().int(),
        invoices: z.number().int(),
        files: z.number().int(),
        bytes: z.number().int(),
    }),
    recordTypes: z.array(mappingValue),
    genders: z.array(mappingValue),
    appointmentNames: z.array(mappingValue),
    issues: z.array(lemniscusIssueSchema),
    errorCount: z.number().int(),
    hintCount: z.number().int(),
});
/** The two files of an export: the ZIP of record folders, and the xlsx. */
export const lemniscusUploadKinds = ['archive', 'records'];
export const lemniscusUploadKindSchema = z.enum(lemniscusUploadKinds);
export const lemniscusUploadSchema = z.object({
    fileName: z.string(),
    sizeBytes: z.number().int(),
    uploadedAt: z.iso.datetime(),
});
/** What an import wrote, by kind of data. */
export const lemniscusImportCountsSchema = z.object({
    contacts: z.number().int(),
    appointments: z.number().int(),
    notes: z.number().int(),
    invoices: z.number().int(),
    files: z.number().int(),
});
/**
 * The steps of a run, in order: the export is read and checked once more,
 * its files are unpacked, then contacts, appointments and notes are written
 * and the files moved into place — the last four inside one transaction.
 */
export const lemniscusRunPhases = [
    'checking',
    'unpacking',
    'contacts',
    'appointments',
    'notes',
    'moving',
];
/** A run that is under way. */
export const lemniscusRunProgressSchema = z.object({
    id: z.uuid(),
    startedAt: z.iso.datetime(),
    phase: z.enum(lemniscusRunPhases),
    /** How far the phase is: files unpacked, directories moved. 0 of 0 where a
     *  phase is one step. */
    done: z.number().int(),
    total: z.number().int(),
});
/**
 * How the last run ended. `failed` and `aborted` both mean that nothing was
 * imported: a failed run hit an error, an aborted one was cut short by a
 * restart of the server.
 */
export const lemniscusRunResultSchema = z.object({
    id: z.uuid(),
    status: z.enum(['completed', 'failed', 'aborted']),
    startedAt: z.iso.datetime().nullable(),
    finishedAt: z.iso.datetime(),
    /** Null where a run ended without them: failed, aborted, or found
     *  completed only after a restart. */
    counts: lemniscusImportCountsSchema.nullable(),
    /** Connects a failed run to its line in the log. */
    errorId: z.string().nullable(),
});
/**
 * Where the import stands — everything the screen draws from.
 *
 * `available` is the one-time rule: an import is possible only while the
 * tenant has no contacts. Nothing else blocks it.
 */
export const lemniscusImportStateSchema = z.object({
    available: z.boolean(),
    /** `IMPORT_FILE_MAX_MB` in bytes, so a file is refused before it is sent. */
    maxFileBytes: z.number().int().positive(),
    archive: lemniscusUploadSchema.nullable(),
    records: lemniscusUploadSchema.nullable(),
    run: lemniscusRunProgressSchema.nullable(),
    lastRun: lemniscusRunResultSchema.nullable(),
    /** Appointments still waiting to be written to the Google calendar. */
    googlePending: z.number().int(),
});
//# sourceMappingURL=lemniscus-import.js.map